import React, { useState } from 'react';
import {
    Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Card, CardContent, Chip, Collapse, Dialog, DialogActions,
    DialogContent, DialogTitle, Divider, IconButton, Link, ListItemText, Menu, MenuItem, Paper, Skeleton, Stack, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import {
    type StavyZarizeni, useKatalogQuery, usePrevzitRozpoznaniMutation, useRozpoznatMutation, useSmazZarizeniMutation, useStavZarizeniQuery,
    useUlozKanalMutation, useZarizeniObjektuQuery, kPrevzeti, znameKomponenty,
} from '../../api/JsdhNotifikaceService/service/IotZarizeniMutations';
import type { IotSenzor, SiaAccount } from '../../api/JsdhNotifikaceService/generated';
import EmptyState from '../admin/EmptyState';
import {
    KATEGORIE, PODPORA_OVLADANI_KRATCE, VYSVETLENI_SCHVALENI, vychoziNazevKanalu, type IotZarizeni, type KatalogModel, type StavZarizeni,
} from '../../api/JsdhNotifikaceService/service/IotZarizeniService';
import ZarizeniDialog from './ZarizeniDialog';
import KanalKarta from './KanalKarta';
import { useTed } from './useTed';
import {
    castiPravidla, cekaNaSchvaleni, jeNedostupneEvidence, jeOvladaneZarizeni, nazevVystupuSKodem, pocetNedostupnychEvidence, popisRozpoznani,
    stavSpojeni, stavSpojeniEvidence, stavSpojeniShelly, stitekSchvaleni, tvarPoctu, vetaPravidla, type StavSpojeni,
} from './iotStav';

/** Nabídka, když model ani rozpoznání komponenty neznají. */
const VSECHNY_KOMPONENTY = ['switch:0', 'switch:1', 'switch:2', 'switch:3', 'light:0', 'light:1', 'light:2', 'light:3', 'cover:0', 'cover:1'];

/** Výstupy, které jde ještě přidat; `zname` = model nebo rozpoznání komponenty zná. */
const volneVystupy = (zarizeni: IotZarizeni, model?: KatalogModel, stav?: StavZarizeni) => {
    const zname = znameKomponenty(model, stav);
    return { zname, volne: (zname.length ? zname : VSECHNY_KOMPONENTY).filter((k) => !zarizeni.kanaly.some((x) => x.komponenta === k)) };
};

const PridatKanal: React.FC<{ zarizeni: IotZarizeni; model?: KatalogModel; stav?: StavZarizeni; onClose: () => void }> = ({ zarizeni, model, stav, onClose }) => {
    const ulozit = useUlozKanalMutation();
    const [komponenta, setKomponenta] = useState('');
    const [nazev, setNazev] = useState('');
    const { zname, volne } = volneVystupy(zarizeni, model, stav);
    const vyber = (k: string) => {
        // Předvyplnit výchozí název, dokud ho uživatel nezměnil.
        if (!nazev.trim() || nazev === (komponenta ? vychoziNazevKanalu(komponenta) : '')) setNazev(k ? vychoziNazevKanalu(k) : '');
        setKomponenta(k);
    };
    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle>Přidat výstup: {zarizeni.nazev}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField select label="Výstup zařízení" value={komponenta} onChange={(e) => vyber(e.target.value)}
                        SelectProps={{ native: true }}>
                        <option value="" />
                        {volne.map((k) => <option key={k} value={k}>{nazevVystupuSKodem(k)}</option>)}
                    </TextField>
                    <TextField label="Název" value={nazev} onChange={(e) => setNazev(e.target.value)} helperText="Například Světlo garáž nebo Vrata" />
                    {zname.length === 0 && (
                        <Alert severity="info">Model zařízení neznáme, proto nabízíme všechny možné výstupy. Vyberte ten, ke kterému je spotřebič připojený.</Alert>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Zrušit</Button>
                <Button variant="contained" disabled={!komponenta || !nazev.trim() || nazev.trim().length > 100 || ulozit.isPending}
                    onClick={() => ulozit.mutate({ id: zarizeni.id, data: { komponenta, nazev: nazev.trim() } }, { onSuccess: onClose })}>Přidat</Button>
            </DialogActions>
        </Dialog>
    );
};

const KLIC_ROZBALENI = (id: number) => `iot-zarizeni-rozbaleno-${id}`;
/** Rozbalení karty se pamatuje jen po dobu relace (pohodlí); úložiště může chybět. */
const nactiRozbaleni = (id: number): boolean | null => {
    try {
        const v = window.sessionStorage.getItem(KLIC_ROZBALENI(id));
        return v == null ? null : v === '1';
    } catch { return null; }
};
const ulozRozbaleni = (id: number, v: boolean) => {
    try { window.sessionStorage.setItem(KLIC_ROZBALENI(id), v ? '1' : '0'); } catch { /* jen pohodlí */ }
};

const Udaj: React.FC<{ nazev: string; children: React.ReactNode }> = ({ nazev, children }) => (
    <>
        <Typography variant="body2" color="text.secondary">{nazev}</Typography>
        <Box sx={{ typography: 'body2', minWidth: 0, overflowWrap: 'anywhere' }}>{children}</Box>
    </>
);

const TechnickeUdaje: React.FC<{ z: IotZarizeni; rozpoznani: string[] }> = ({ z, rozpoznani }) => {
    const model = [z.vyrobce, z.modelNazev].filter(Boolean).join(' ');
    return (
        <Accordion disableGutters elevation={0} variant="outlined" sx={{ mt: 1.5, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="body2">Technické údaje</Typography></AccordionSummary>
            <AccordionDetails>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '160px 1fr' }, columnGap: 2, rowGap: { xs: 0, sm: 0.5 } }}>
                    {(model || z.overeno !== true) && (
                        <Udaj nazev="Výrobce a model">{model || 'neuveden'}{z.overeno !== true ? ' (Neověřený model)' : ''}</Udaj>
                    )}
                    <Udaj nazev="Druh">{KATEGORIE[z.kategorie] ?? z.kategorie}</Udaj>
                    <Udaj nazev="ID zařízení">{z.identifikator}</Udaj>
                    {z.vyrobniCislo && <Udaj nazev="Výrobní číslo">{z.vyrobniCislo}</Udaj>}
                    {z.datumInstalace && <Udaj nazev="Instalováno">{new Date(z.datumInstalace).toLocaleDateString('cs-CZ')}</Udaj>}
                    {z.kanaly.length > 0 && (
                        <Udaj nazev="Výstupy">{z.kanaly.map((k) => <div key={k.id}>{k.nazev}: {nazevVystupuSKodem(k.komponenta)}</div>)}</Udaj>
                    )}
                    {rozpoznani.length > 0 && <Udaj nazev="Rozpoznání">{rozpoznani.map((r) => <div key={r}>{r}</div>)}</Udaj>}
                    {z.poznamka && <Udaj nazev="Poznámka">{z.poznamka}</Udaj>}
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};

const ZarizeniKarta: React.FC<{
    z: IotZarizeni; katalog: KatalogModel[]; onUpravit: () => void; onOdebrat: () => void; poll?: boolean;
}> = ({ z, katalog, onUpravit, onOdebrat, poll = true }) => {
    const shelly = z.protokol === 'SHELLY_MQTT';
    // poll=false: stav dotazuje stránka (jediný časovač), karta jen čte sdílenou cache (review K6-K9, Q2).
    const { data: stav, isError } = useStavZarizeniQuery(z.id, shelly, poll);
    const rozpoznat = useRozpoznatMutation();
    const prevzit = usePrevzitRozpoznaniMutation();
    const [pridatKanal, setPridatKanal] = useState(false);
    const [menu, setMenu] = useState<HTMLElement | null>(null);
    const [rozbaleno, setRozbaleno] = useState<boolean>(() => nactiRozbaleni(z.id) ?? false);
    const ted = useTed();
    const model = katalog.find((m) => m.kod === z.modelKod);
    const prevzeti = kPrevzeti(z, stav, katalog);
    const lzePrevzit = shelly && !!stav && (!!prevzeti.model || prevzeti.kanaly.length > 0);
    const spojeni = shelly ? stavSpojeniShelly(stav, isError, ted) : stavSpojeni(null);
    const rozpoznani = shelly ? popisRozpoznani(stav, ted) : [];
    const stitky = stitekSchvaleni(z);
    const idObsahu = `zarizeni-${z.id}-obsah`;
    const idMenu = `zarizeni-${z.id}-menu`;
    const { zname, volne } = volneVystupy(z, model, stav);
    const vsechnyPridane = zname.length > 0 && volne.length === 0;
    const nazevModelu = [z.vyrobce, z.modelNazev].filter(Boolean).join(' ') || (KATEGORIE[z.kategorie] ?? z.kategorie);
    const prepni = () => setRozbaleno((r) => { ulozRozbaleni(z.id, !r); return !r; });
    const zavriMenu = () => setMenu(null);

    return (
        <Card variant="outlined" id={'zarizeni-' + z.id} data-testid={'zarizeni-' + z.id}>
            <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <IconButton size="small" onClick={prepni} aria-expanded={rozbaleno} aria-controls={idObsahu}
                        aria-label={rozbaleno ? `Sbalit ${z.nazev}` : `Rozbalit ${z.nazev}`}
                        sx={{ mt: 0.25, transform: rozbaleno ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}>
                        <ExpandMoreIcon fontSize="small" />
                    </IconButton>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', columnGap: 2, rowGap: 0.5, flexWrap: 'wrap' }}>
                            {/* Klik na název rozbalí kartu taky (větší cíl na tabletu, D7); pro klávesnici a čtečky zůstává tlačítko se šipkou. */}
                            <Typography variant="subtitle1" component="h3" onClick={prepni} sx={{ fontWeight: 600, cursor: 'pointer' }}>{z.nazev}</Typography>
                            {z.umisteni && <Typography variant="body2" color="text.secondary">{z.umisteni}</Typography>}
                            {spojeni.druh !== 'bez-stavu' && (
                                <Tooltip title={spojeni.tooltip ?? ''}>
                                    <Typography variant="body2" data-testid={'spojeni-' + z.id} tabIndex={spojeni.tooltip ? 0 : undefined}
                                        color={spojeni.barva === 'warning' ? 'warning.main' : 'text.secondary'}>{spojeni.text}</Typography>
                                </Tooltip>
                            )}
                        </Box>
                        <Typography variant="body2" color="text.secondary">{nazevModelu}</Typography>
                        {stitky.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                                {stitky.map((s) => (
                                    <Tooltip key={s.klic} title={s.tooltip ?? ''}>
                                        <Chip size="small" color={s.barva} variant={s.variant} label={s.text} tabIndex={s.tooltip ? 0 : undefined} />
                                    </Tooltip>
                                ))}
                            </Box>
                        )}
                    </Box>
                    <IconButton size="small" aria-label={`Další akce zařízení ${z.nazev}`} aria-haspopup="menu" aria-expanded={!!menu}
                        aria-controls={menu ? idMenu : undefined} onClick={(e) => setMenu(e.currentTarget)}>
                        <MoreVertIcon fontSize="small" />
                    </IconButton>
                </Box>

                {!rozbaleno && z.kanaly.length > 0 && (
                    <Box sx={{ mt: 1, pl: 5 }} data-testid={'zarizeni-souhrn-' + z.id}>
                        {z.kanaly.map((k) => {
                            const c = castiPravidla(k.typ, k.pravidlo);
                            return (
                                <Typography key={k.id} variant="body2" color={!c || c.vypnuto || !k.aktivni ? 'text.secondary' : 'text.primary'}>
                                    {k.nazev}: při poplachu {c ? `${vetaPravidla(c)}${c.vypnuto ? ' · vypnuto' : ''}` : 'nenastaveno'}
                                </Typography>
                            );
                        })}
                    </Box>
                )}

                <Collapse in={rozbaleno} unmountOnExit id={idObsahu}>
                    <Stack spacing={1} sx={{ mt: 1.5 }}>
                        {z.kanaly.map((k) => (
                            <KanalKarta key={k.id} zarizeni={z} kanal={k} model={model} spojeni={spojeni} posledniKontaktMs={stav?.posledniKontaktEpochMs}
                                stav={stav?.kanaly?.find((s) => s.komponenta === k.komponenta && !s.faze)} />
                        ))}
                    </Stack>
                    <TechnickeUdaje z={z} rozpoznani={rozpoznani} />
                </Collapse>
            </CardContent>

            <Menu id={idMenu} anchorEl={menu} open={!!menu} onClose={zavriMenu}>
                <MenuItem onClick={() => { zavriMenu(); onUpravit(); }}><ListItemText>Upravit údaje</ListItemText></MenuItem>
                {!shelly && (
                    <Tooltip title={PODPORA_OVLADANI_KRATCE}>
                        <span tabIndex={0} data-testid="pridat-vystup-nepodporovano">
                            <MenuItem disabled><ListItemText primary="Přidat výstup" /></MenuItem>
                        </span>
                    </Tooltip>
                )}
                {shelly && (
                    <MenuItem disabled={vsechnyPridane} onClick={() => { zavriMenu(); setPridatKanal(true); }}>
                        <ListItemText primary="Přidat výstup" secondary={vsechnyPridane ? 'Všechny výstupy zařízení už jsou přidané' : undefined} />
                    </MenuItem>
                )}
                {shelly && (
                    <MenuItem disabled={rozpoznat.isPending} onClick={() => { zavriMenu(); rozpoznat.mutate(z.id); }}>
                        <ListItemText>Rozpoznat zařízení</ListItemText>
                    </MenuItem>
                )}
                {lzePrevzit && stav && (
                    <MenuItem disabled={prevzit.isPending} onClick={() => { zavriMenu(); prevzit.mutate({ zarizeni: z, stav, katalog }); }}>
                        <ListItemText>Převzít z rozpoznání</ListItemText>
                    </MenuItem>
                )}
                <Divider />
                <MenuItem sx={{ color: 'error.main' }} onClick={() => { zavriMenu(); onOdebrat(); }}><ListItemText>Odebrat zařízení</ListItemText></MenuItem>
            </Menu>
            {pridatKanal && <PridatKanal zarizeni={z} model={model} stav={stav} onClose={() => setPridatKanal(false)} />}
        </Card>
    );
};

/** Druh filtru ze souhrnu záložky (4.2); druhý klik na stejný čip filtr zruší. */
type Filtr = 'nedostupna' | 'pravidlo' | 'ceka';

export interface ZarizeniSectionProps {
    objektId: number;
    /** Název objektu do nadpisu dialogu Přidat zařízení. */
    objektNazev?: string;
    /** Senzory objektu (už načtené v detailu objektu): stav čidel ve skupině B. */
    senzory?: IotSenzor[];
    /** SIA účty objektu: stav ústředen ve skupině B. */
    siaUcty?: SiaAccount[];
    /** Přepnutí na záložku Senzory / EPS/EZS a posun na kartu (kotva = id prvku). */
    onPrejit?: (zalozka: 'senzory' | 'eps', kotva: string) => void;
    /**
     * Stavy ovládaných Shelly zařízení, které už dotazuje stránka. Když jsou předané, sekce ani karty
     * nespouštějí vlastní časovač (review K6-K9, Q2) a seznam zařízení objektu dotazuje taky stránka (E3);
     * bez nich si stavy dotazují karty a seznam sekce.
     */
    stavy?: StavyZarizeni;
}

const SkupinaNadpis: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography variant="overline" component="h2" color="text.secondary" sx={{ display: 'block', mt: 3, mb: 1, lineHeight: 1.5 }}>{children}</Typography>
);

const TextSpojeni: React.FC<{ s: StavSpojeni }> = ({ s }) => (s.druh === 'bez-stavu' ? null : (
    <Tooltip title={s.tooltip ?? ''}>
        <Box component="span" tabIndex={s.tooltip ? 0 : undefined} sx={{ color: s.barva === 'warning' ? 'warning.main' : 'text.secondary' }}>{s.text}</Box>
    </Tooltip>
));

const ZarizeniSection: React.FC<ZarizeniSectionProps> = ({ objektId, objektNazev, senzory = [], siaUcty = [], onPrejit, stavy: stavyZvenku }) => {
    // Se stavy zvenku drží časovače stránka (i seznam zařízení, E3): sekce jen čte sdílenou cache, žádný druhý poller.
    const { data = [], isLoading, error, refetch } = useZarizeniObjektuQuery(objektId, !stavyZvenku);
    const { data: katalog = [] } = useKatalogQuery();
    const smazat = useSmazZarizeniMutation();
    const theme = useTheme();
    const uzky = useMediaQuery(theme.breakpoints.down('sm'));
    const [editace, setEditace] = useState<IotZarizeni | 'nove' | null>(null);
    const [kOdebrani, setKOdebrani] = useState<IotZarizeni | null>(null);
    const [filtr, setFiltr] = useState<Filtr | null>(null);
    const [menuRadku, setMenuRadku] = useState<{ el: HTMLElement; z: IotZarizeni } | null>(null);
    const ted = useTed();

    const ovladane = data.filter(jeOvladaneZarizeni);
    const ostatni = data.filter((z) => !jeOvladaneZarizeni(z));

    const senzorZarizeni = (z: IotZarizeni) => (z.senzorId != null ? senzory.find((s) => s.id === z.senzorId) : undefined);
    const siaZarizeni = (z: IotZarizeni) => (z.siaAccountId != null ? siaUcty.find((a) => a.id === z.siaAccountId) : undefined);
    // Skupina B: stav ze záznamu evidence, stejně jako v Evidenci (E7); bez vazby na čidlo „Zatím se neozvalo“.
    const spojeniZarizeni = (z: IotZarizeni): StavSpojeni => stavSpojeniEvidence(z, ted);
    const sPravidlem = (z: IotZarizeni) => z.kanaly.some((k) => !!k.pravidlo?.aktivni);
    const vyhovuje = (z: IotZarizeni) => !filtr
        || (filtr === 'nedostupna' && jeNedostupneEvidence(z))
        || (filtr === 'pravidlo' && sPravidlem(z))
        || (filtr === 'ceka' && cekaNaSchvaleni(z));
    const poradi = (z: IotZarizeni) => (cekaNaSchvaleni(z) ? 0 : jeNedostupneEvidence(z) ? 1 : 2);
    const skupinaA = ovladane.filter(vyhovuje).sort((a, b) => poradi(a) - poradi(b) || a.nazev.localeCompare(b.nazev, 'cs'));
    const skupinaB = ostatni.filter(vyhovuje);

    // Stejný helper jako hlavička objektu (E1): obě čísla se nemůžou rozejít.
    const pocetNedostupnych = pocetNedostupnychEvidence(data);
    const pocetSPravidlem = data.filter(sPravidlem).length;
    const pocetCeka = data.filter(cekaNaSchvaleni).length;
    const prepniFiltr = (f: Filtr) => setFiltr((x) => (x === f ? null : f));

    const pridat = <Button variant="contained" onClick={() => setEditace('nove')}>Přidat zařízení</Button>;

    if (isLoading) {
        return (
            <Stack spacing={2} data-testid="zarizeni-nacitani">
                {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={76} />)}
            </Stack>
        );
    }
    if (error) {
        return (
            <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => refetch()}>Zkusit znovu</Button>}>
                Zařízení se nepodařilo načíst. Zkuste stránku obnovit.
            </Alert>
        );
    }

    const nazevB = (z: IotZarizeni) => senzorZarizeni(z)?.nazev || siaZarizeni(z)?.nazev || z.nazev;
    const umisteniB = (z: IotZarizeni) => senzorZarizeni(z)?.umisteni || z.umisteni;
    const odkazB = (z: IotZarizeni) => {
        if (senzorZarizeni(z) && z.senzorId != null) {
            const id = z.senzorId;
            return <Button size="small" endIcon={<ChevronRightIcon />} onClick={() => onPrejit?.('senzory', `senzor-${id}`)}>Senzory</Button>;
        }
        if (siaZarizeni(z) && z.siaAccountId != null) {
            const id = z.siaAccountId;
            return <Button size="small" endIcon={<ChevronRightIcon />} onClick={() => onPrejit?.('eps', `sia-${id}`)}>EPS/EZS</Button>;
        }
        return null;
    };
    const menuB = (z: IotZarizeni) => (
        <IconButton size="small" aria-label={`Další akce zařízení ${nazevB(z)}`} onClick={(e) => setMenuRadku({ el: e.currentTarget, z })}>
            <MoreVertIcon fontSize="small" />
        </IconButton>
    );

    return (
        <Box>
            {data.length === 0 ? (
                <Paper variant="outlined">
                    <EmptyState
                        icon={<DevicesOtherIcon />}
                        title="V objektu zatím není žádné zařízení"
                        description="Přidejte spínače, stmívače nebo rolety, které se mají při poplachu ovládat. Čidla a ústředny se sem doplní z evidence."
                        action={pridat}
                    />
                </Paper>
            ) : (
                <>
                    <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }} data-testid="zarizeni-souhrn">
                        <Typography variant="subtitle1" component="span" sx={{ fontWeight: 600, mr: 1 }}>
                            {data.length} zařízení
                        </Typography>
                        {pocetNedostupnych > 0 && (
                            <Chip color="warning" variant={filtr === 'nedostupna' ? 'filled' : 'outlined'} aria-pressed={filtr === 'nedostupna'}
                                label={`${pocetNedostupnych} ${tvarPoctu(pocetNedostupnych, 'nedostupné', 'nedostupná', 'nedostupných')}`}
                                onClick={() => prepniFiltr('nedostupna')} />
                        )}
                        <Chip variant={filtr === 'pravidlo' ? 'filled' : 'outlined'} aria-pressed={filtr === 'pravidlo'}
                            label={`${pocetSPravidlem} s pravidlem při poplachu`} onClick={() => prepniFiltr('pravidlo')}
                            sx={pocetSPravidlem === 0 ? { color: 'text.secondary' } : undefined} />
                        {pocetCeka > 0 && (
                            <Tooltip title={VYSVETLENI_SCHVALENI}>
                                <Chip color="warning" variant={filtr === 'ceka' ? 'filled' : 'outlined'} aria-pressed={filtr === 'ceka'}
                                    label={`${pocetCeka} ${tvarPoctu(pocetCeka, 'čeká', 'čekají', 'čeká')} na schválení správcem`} onClick={() => prepniFiltr('ceka')} />
                            </Tooltip>
                        )}
                        <Box sx={{ flexGrow: 1 }} />
                        {pridat}
                    </Paper>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Relé Raspberry Pi pro ústřednu jsou v záložce EPS/EZS.
                    </Typography>

                    {skupinaA.length === 0 && skupinaB.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                            Žádné zařízení neodpovídá výběru.{' '}
                            <Link component="button" variant="body2" onClick={() => setFiltr(null)}>Zobrazit vše</Link>
                        </Typography>
                    )}

                    {skupinaA.length > 0 && (
                        <Box data-testid="skupina-ovladane">
                            <SkupinaNadpis>Ovládané při poplachu ({skupinaA.length})</SkupinaNadpis>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{PODPORA_OVLADANI_KRATCE}</Typography>
                            <Stack spacing={2}>
                                {skupinaA.map((z) => (
                                    <ZarizeniKarta key={z.id} z={z} katalog={katalog} poll={!stavyZvenku} onUpravit={() => setEditace(z)} onOdebrat={() => setKOdebrani(z)} />
                                ))}
                            </Stack>
                        </Box>
                    )}

                    {skupinaB.length > 0 && (
                        <Box data-testid="skupina-cidla">
                            <SkupinaNadpis>Čidla, měřiče a ústředny ({skupinaB.length})</SkupinaNadpis>
                            {uzky ? (
                                <Paper variant="outlined">
                                    {skupinaB.map((z, i) => (
                                        <Box key={z.id} id={'zarizeni-' + z.id}
                                            sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, borderTop: i > 0 ? 1 : 0, borderColor: 'divider' }}>
                                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{nazevB(z)}</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {[KATEGORIE[z.kategorie] ?? z.kategorie, umisteniB(z)].filter(Boolean).join(' · ')}
                                                    {spojeniZarizeni(z).druh !== 'bez-stavu' && <> · <TextSpojeni s={spojeniZarizeni(z)} /></>}
                                                </Typography>
                                                {odkazB(z)}
                                            </Box>
                                            {menuB(z)}
                                        </Box>
                                    ))}
                                </Paper>
                            ) : (
                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Název</TableCell>
                                                <TableCell>Druh</TableCell>
                                                <TableCell>Umístění</TableCell>
                                                <TableCell>Stav</TableCell>
                                                <TableCell />
                                                <TableCell padding="checkbox" />
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {skupinaB.map((z) => (
                                                <TableRow key={z.id} id={'zarizeni-' + z.id} data-testid={'zarizeni-' + z.id}>
                                                    <TableCell>{nazevB(z)}</TableCell>
                                                    <TableCell>{KATEGORIE[z.kategorie] ?? z.kategorie}</TableCell>
                                                    <TableCell>{umisteniB(z) || '–'}</TableCell>
                                                    <TableCell><TextSpojeni s={spojeniZarizeni(z)} /></TableCell>
                                                    <TableCell align="right">{odkazB(z)}</TableCell>
                                                    <TableCell padding="checkbox">{menuB(z)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                    )}
                </>
            )}

            <Menu anchorEl={menuRadku?.el} open={!!menuRadku} onClose={() => setMenuRadku(null)}>
                <MenuItem onClick={() => { const z = menuRadku?.z; setMenuRadku(null); if (z) setEditace(z); }}>
                    <ListItemText>Upravit záznam v evidenci</ListItemText>
                </MenuItem>
                <Divider />
                <MenuItem sx={{ color: 'error.main' }} onClick={() => { const z = menuRadku?.z; setMenuRadku(null); if (z) setKOdebrani(z); }}>
                    <ListItemText>Odebrat z evidence</ListItemText>
                </MenuItem>
            </Menu>
            {editace && (
                <ZarizeniDialog objektId={objektId} objektNazev={objektNazev} zarizeni={editace === 'nove' ? undefined : editace}
                    onClose={() => setEditace(null)} />
            )}
            <Dialog open={!!kOdebrani} onClose={() => setKOdebrani(null)}>
                <DialogTitle>Odebrat zařízení?</DialogTitle>
                <DialogContent><Typography variant="body2">Zařízení {kOdebrani?.nazev} se odebere i s výstupy a pravidly. Tuto akci nelze vrátit.</Typography></DialogContent>
                <DialogActions>
                    <Button variant="contained" autoFocus onClick={() => setKOdebrani(null)}>Zrušit</Button>
                    <Button color="error" disabled={smazat.isPending}
                        onClick={() => { if (kOdebrani) smazat.mutate(kOdebrani.id, { onSettled: () => setKOdebrani(null) }); }}>Odebrat</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ZarizeniSection;
